import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, WritableSignal } from '@angular/core'
import { TableLazyLoadEvent, TableModule } from 'primeng/table'
import { CardModule } from 'primeng/card'
import { CommonModule, DatePipe } from '@angular/common'
import { hasActualValue } from '@core/utils/form-utils'
import { rxResource } from '@angular/core/rxjs-interop'
import { of } from 'rxjs'
import { Tag } from 'primeng/tag'
import { ViewportService } from '@services/viewport.service'
import { PolicyService } from '@features/insured/services/policy/policy.service'
import {
  AircraftScheduleApiResponse,
  EquipmentScheduleApiResponse,
  PolicyAssetsApiResponse,
  ScheduleGroup,
  ScheduleSort,
  StripedScheduleRow
} from '@features/insured/models/policy/policy.model'
import { FormatCurrencyAmountPipe } from '@shared/pipes/format-currency-amount.pipe'
import { aircraftScheduleUrl, equipmentScheduleUrl } from '@core/utils/origami-utils'

/** Columns holding amounts; every other column (identifiers, types, status) sorts as text. */
const NUMERIC_SCHEDULE_FIELDS: ReadonlySet<PropertyKey> = new Set(['mtow', 'leaseValue', 'tpllWarExcessLimit'])

type ScheduleSortValue = string | number | null

/** Narrows a schedule cell to a sortable primitive; anything else (dates, objects) counts as empty. */
function toSortValue(value: unknown): ScheduleSortValue {
  return typeof value === 'string' || typeof value === 'number' ? value : null
}

/**
 * Compares two cell values, keeping empty values at the bottom regardless of the sort order. Numeric
 * columns compare by value (MTOW "9000" before "70000"); all others compare as plain text, so serial
 * numbers like "0767" and "60B389" sort by their characters rather than by a leading number.
 */
function compareScheduleValues(a: ScheduleSortValue, b: ScheduleSortValue, order: 1 | -1, numeric: boolean): number {
  const aEmpty = !hasActualValue(a)
  const bEmpty = !hasActualValue(b)
  if (aEmpty || bEmpty) return Number(aEmpty) - Number(bEmpty)

  const aNumber = Number(a)
  const bNumber = Number(b)
  const result = numeric && !Number.isNaN(aNumber) && !Number.isNaN(bNumber)
    ? aNumber - bNumber
    : String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })

  return result * order
}

/**
 * Shows a policy's insured objects: the Fleet List (aircraft) and the Equipment List, as sortable
 * tables on desktop and as one card per aircraft or equipment on mobile.
 *
 * @example
 * <albert-fleet-list [policyId]="policyId" [rendered]="isOpen" />
 */
@Component({
  selector: 'albert-fleet-list',
  imports: [
    CommonModule,
    TableModule,
    CardModule,
    Tag,
    FormatCurrencyAmountPipe,
    DatePipe
  ],
  templateUrl: './fleet-list.component.html',
  styleUrl: './fleet-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class FleetListComponent {
  /** Policy whose assets are listed; nothing is loaded while it is empty */
  public readonly policyId = input<string | null | undefined>()
  /** Whether the lists are rendered, e.g. while the surrounding panel is open */
  public readonly rendered = input<boolean>(false)
  /** Fleet List defaults to Registration, Equipment List to SN; both can be re-sorted by any column. */
  // Linked to `policyId`: the component is reused across insureds, so switching insured resets the sort.
  public readonly aircraftSort = linkedSignal<string | null | undefined, ScheduleSort<AircraftScheduleApiResponse>>({
    source: this.policyId,
    computation: () => ({ field: 'registration', order: 1 })
  })
  public readonly equipmentSort = linkedSignal<string | null | undefined, ScheduleSort<EquipmentScheduleApiResponse>>({
    source: this.policyId,
    computation: () => ({ field: 'serialNumber', order: 1 })
  })
  private readonly policyService = inject(PolicyService)
  private readonly viewportService = inject(ViewportService)
  /** Whether the cards are shown instead of the tables */
  public readonly isMobile = this.viewportService.isMobile
  private readonly assetsResource = rxResource<PolicyAssetsApiResponse, string | null | undefined>({
    params: () => this.policyId(),

    stream: ({ params: id }) => {
      if (hasActualValue(id)) {
        return this.policyService.getAssets(id)
      }
      return of({ aircraftSchedules: [], equipmentSchedules: [] })
    }
  })
  // Rows of one aircraft are kept together; lease value columns (amounts and their period) sort within it.
  private readonly sortedAircraftSchedules = computed(() =>
    this.sortScheduleList(
      this.assetsResource.value()?.aircraftSchedules,
      ['registration', 'serialNumber'],
      ['leaseValue', 'tpllWarExcessLimit', 'beginOfValueDetail', 'endOfValueDetail'],
      this.aircraftSort(),
      'registration'
    )
  )
  /** Fleet List table rows: identity columns blanked on an aircraft's follow-up rows, striped per aircraft */
  public readonly aircraftSchedules = computed(() =>
    this.formatScheduleList(
      this.sortedAircraftSchedules(),
      ['registration', 'serialNumber'],
      ['aircraftType', 'registration', 'serialNumber', 'mtow', 'objectStatus'],
      ['leaseValue', 'tpllWarExcessLimit']
    )
  )
  /** Fleet List cards: one group per aircraft with its rows as variants */
  public readonly aircraftGroups = computed(() =>
    this.groupScheduleList(
      this.sortedAircraftSchedules(),
      ['registration', 'serialNumber'],
      ['leaseValue', 'tpllWarExcessLimit']
    )
  )
  // Rows of one equipment are kept together; lease value and its period sort within it.
  private readonly sortedEquipmentSchedules = computed(() =>
    this.sortScheduleList(
      this.assetsResource.value()?.equipmentSchedules,
      ['serialNumber'],
      ['leaseValue', 'activeDate', 'inactiveDate'],
      this.equipmentSort(),
      'serialNumber'
    )
  )
  /** Equipment List table rows: identity columns blanked on follow-up rows, striped per equipment */
  public readonly equipmentSchedules = computed(() =>
    this.formatScheduleList(
      this.sortedEquipmentSchedules(),
      ['serialNumber'],
      ['equipmentCategory', 'equipmentType', 'serialNumber', 'objectStatus'],
      ['leaseValue']
    )
  )
  /** Equipment List cards: one group per equipment with its rows as variants */
  public readonly equipmentGroups = computed(() =>
    this.groupScheduleList(
      this.sortedEquipmentSchedules(),
      ['serialNumber'],
      ['leaseValue']
    )
  )

  // The tables run in lazy mode so PrimeNG only reports the sort state; sorting happens per group in
  // `sortScheduleList`, because a plain row sort would tear apart the rows of one aircraft/equipment.
  /**
   * Takes over a sort change from the Fleet List table.
   *
   * @param event - PrimeNG lazy-load event carrying the sort field and order
   */
  public onAircraftSort(event: TableLazyLoadEvent): void {
    this.updateSort(this.aircraftSort, event)
  }

  /**
   * Takes over a sort change from the Equipment List table.
   *
   * @param event - PrimeNG lazy-load event carrying the sort field and order
   */
  public onEquipmentSort(event: TableLazyLoadEvent): void {
    this.updateSort(this.equipmentSort, event)
  }

  /**
   * Builds the link to an aircraft schedule in Origami.
   *
   * @param externalId - Origami identifier of the aircraft schedule
   * @returns URL of the aircraft schedule in Origami
   */
  public aircraftScheduleUrl(externalId: string): string {
    return aircraftScheduleUrl(externalId)
  }

  /**
   * Builds the link to an equipment schedule in Origami.
   *
   * @param externalId - Origami identifier of the equipment schedule
   * @returns URL of the equipment schedule in Origami
   */
  public equipmentScheduleUrl(externalId: string): string {
    return equipmentScheduleUrl(externalId)
  }

  /**
   * Maps an object status to the colour of its status tag.
   *
   * @param status - Object status, e.g. ACTIVE, PLANNED or TBA
   * @returns PrimeNG tag severity for the status
   */
  public getStatusSeverity(status: string): 'success' | 'warn' | 'secondary' | 'contrast' | 'danger' {
    switch (status) {
      case 'ACTIVE':
        return 'success'
      case 'PLANNED':
        return 'warn'
      case 'TBA':
        return 'secondary'
      case '':
        return 'contrast'
      default:
        return 'danger'
    }
  }

  private updateSort<T>(sort: WritableSignal<ScheduleSort<T>>, event: TableLazyLoadEvent): void {
    if (typeof event.sortField !== 'string') return

    const next: ScheduleSort<T> = { field: event.sortField as keyof T, order: event.sortOrder === -1 ? -1 : 1 }
    const current = sort()
    if (current.field !== next.field || current.order !== next.order) {
      sort.set(next)
    }
  }

  /**
   * Sorts whole schedule groups (rows sharing the identity keys) so they stay adjacent. When the sort
   * field varies per row (e.g. Lease Value), the rows inside each group are sorted too and the group
   * is ranked by its first row.
   */
  private sortScheduleList<T extends object>(
    list: T[] | undefined,
    identityKeys: (keyof T)[],
    variantKeys: (keyof T)[],
    sort: ScheduleSort<T>,
    tiebreakKey: keyof T
  ): T[] {
    if (!list) return []

    const groups = new Map<string, T[]>()
    for (const item of list) {
      const identity = identityKeys.map(key => toSortValue(item[key]) ?? '').join('|')
      const group = groups.get(identity)
      if (group) {
        group.push(item)
      } else {
        groups.set(identity, [item])
      }
    }

    const { field, order } = sort
    const numeric = NUMERIC_SCHEDULE_FIELDS.has(field)
    const sortedGroups = [...groups.values()].map(variants =>
      variantKeys.includes(field)
        ? [...variants].sort((a, b) => compareScheduleValues(toSortValue(a[field]), toSortValue(b[field]), order, numeric))
        : variants
    )

    sortedGroups.sort((a, b) =>
      compareScheduleValues(toSortValue(a[0]![field]), toSortValue(b[0]![field]), order, numeric)
      || compareScheduleValues(toSortValue(a[0]![tiebreakKey]), toSortValue(b[0]![tiebreakKey]), 1, false)
    )

    return sortedGroups.flat()
  }

  /**
   * Prepares rows for a table: fills empty amounts with '-', blanks the given columns on every row
   * that repeats the previous row's identity, and stripes rows per group rather than per row.
   */
  private formatScheduleList<T extends object>(
    list: T[] | undefined,
    identityKeys: (keyof T)[],
    clearKeys: (keyof T)[],
    fallbackKeys: (keyof T)[]
  ): StripedScheduleRow<T>[] {
    if (!list) return []

    let groupIndex = -1

    return list.map((item, index) => {
      const updated = this.applyFallback(item, fallbackKeys)
      const prev = index > 0 ? list[index - 1] : undefined
      const isDuplicate = prev !== undefined && identityKeys.every(key => item[key] === prev[key])

      if (isDuplicate) {
        for (const key of clearKeys) {
          (updated as Record<keyof T, unknown>)[key] = ''
        }
      } else {
        groupIndex++
      }

      return { ...updated, striped: groupIndex % 2 === 1 }
    })
  }

  /** Collects consecutive rows with the same identity into one group per aircraft or equipment, for the cards. */
  private groupScheduleList<T extends object>(
    list: T[] | undefined,
    identityKeys: (keyof T)[],
    fallbackKeys: (keyof T)[]
  ): ScheduleGroup<T>[] {
    if (!list) return []

    const groups: ScheduleGroup<T>[] = []

    for (const item of list) {
      const entry = this.applyFallback(item, fallbackKeys)
      const lastGroup = groups.at(-1)
      const sameIdentity = lastGroup !== undefined &&
        identityKeys.every(key => lastGroup.shared[key] === item[key])

      if (sameIdentity) {
        lastGroup.variants.push(entry)
      } else {
        groups.push({ shared: entry, variants: [entry] })
      }
    }

    return groups
  }

  private applyFallback<T extends object>(item: T, fallbackKeys: (keyof T)[]): T {
    const updated = { ...item }

    for (const key of fallbackKeys) {
      if (!hasActualValue(updated[key])) {
        (updated as Record<keyof T, unknown>)[key] = '-'
      }
    }

    return updated
  }

}
